// RedemptionsPanel — the Refer & Save section's Redemptions tab (account-area
// redesign Task 10; the 2026-10-02 amendment F11/F18-F20): the credit
// summary, the primary "Redeem credits" action opening RedeemPanel's dialog,
// and ONE merged history table.
//
// This panel ABSORBS and replaces ReferralPayoutsPanel.tsx (the pre-task
// "Payouts" card list — deleted by this task; its §30 amount pins travel
// verbatim as RedemptionAmounts below, and its file name carried the retired
// "Payouts" vocabulary the redesign retires everywhere). The old
// "New Request" form is gone with it: redemption requests are raised through
// the RedeemDialog (store select first, the store's merchant account
// resolved read-only — see RedeemPanel.tsx's own header for the contract and
// the byte-identical disclosure gate).
//
// The pending-row cancel is new UI over the existing If-Match API
// (cancelMyRedemption — §4.4.1: `pending` is the ONLY merchant-cancellable
// status). After a cancel the row re-seeds from the MUTATION RESPONSE
// (§59 — no list re-fetch) and the balance is re-read, because a cancelled
// request releases its hold on the spendable figure.
//
// Malformed-payload guards are LOAD-BEARING here: the AccountCenter test
// double (and any 501/unimplemented route) can hand this panel an ARRAY for
// balance/rows/mids — every read below validates its shape before use, so a
// shell test renders "—", never "$NaN".
import { useCallback, useEffect, useState } from "react";
import {
  EmbedBanner,
  EmbedButton,
  EmbedCard,
  EmbedHeading,
  EmbedLink,
  EmbedStatusBadge,
  EmbedTable,
  EmbedText,
  useEmbedTokens,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { formatCurrency } from "../../embed-shared/currency";
import { formatStatementMonth } from "../../embed-shared/format";
import type { TokenSet } from "../../embed-shared/tokens";
import type { SectionId } from "./account-sections";
import { listMyMIDs, type MerchantMIDWire } from "./mid-api";
import {
  cancelMyRedemption,
  getMyReferralBalance,
  listMyRedemptions,
  type ReferralBalanceWire,
  type ReferralRedemptionWire,
} from "./referral-api";
import { RedeemDialog } from "./RedeemPanel";

// §30 from birth (moved verbatim from ReferralPayoutsPanel.tsx — the pins in
// RedemptionsPanel.test.tsx keep these labels byte-identical): verified
// differs from requested → BOTH labeled; equal → verified once; absent →
// requested. Label and value render as SEPARATE text nodes (label muted,
// value emphasized) so tests can pin a figure without matching the label.
// Values are decimal strings on the wire and render through the ONE money
// formatter — never a raw "80.33".
function amountLabel(t: TokenSet, label: string, value: string, strong: boolean) {
  return (
    <span style={{ display: "inline-flex", gap: t.spacing.xs, alignItems: "baseline" }}>
      <span
        style={{
          fontSize: t.fontSize.xs,
          fontWeight: 500,
          color: t.colors.textSecondary,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: t.fontSize.sm,
          fontWeight: strong ? 600 : 500,
        }}
      >
        {formatCurrency(Number(value))}
      </span>
    </span>
  );
}

export function RedemptionAmounts({ row }: { row: ReferralRedemptionWire }) {
  const t = useEmbedTokens();
  if (row.verified_amount !== undefined) {
    if (row.verified_amount !== row.requested_amount) {
      return (
        <span style={{ display: "inline-flex", gap: t.spacing.md, flexWrap: "wrap" }}>
          {amountLabel(t, "Requested", row.requested_amount, false)}
          {amountLabel(t, "Verified", row.verified_amount, true)}
        </span>
      );
    }
    return amountLabel(t, "Verified", row.verified_amount, true);
  }
  return amountLabel(t, "Requested", row.requested_amount, false);
}

/** A wire value that is only usable when it is the expected shape. */
function spendableOf(balance: ReferralBalanceWire | null): number | null {
  if (!balance || typeof balance.spendable !== "string") return null;
  const n = Number(balance.spendable);
  return Number.isFinite(n) ? n : null;
}

function holdingOf(balance: ReferralBalanceWire | null): number | null {
  if (!balance || typeof balance.holding !== "string") return null;
  const n = Number(balance.holding);
  return Number.isFinite(n) ? n : null;
}

export interface RedemptionsPanelProps {
  /** The redeem dialog's "Link a merchant account in Merchant services"
   *  escape hatch — routes to the Merchant services section (the shell's
   *  navigate). Absent (standalone mounts) hides the link. */
  onNavigateSection?: (id: SectionId) => void;
  /** Forwarded to the dialog (TD-605): false = the member lacks manage-mids,
   *  so the dialog never reads GET /me/mids and redeems by store. */
  canViewAccounts?: boolean;
}

export function RedemptionsPanel({ onNavigateSection, canViewAccounts = true }: RedemptionsPanelProps) {
  const t = useEmbedTokens();

  const [balance, setBalance] = useState<ReferralBalanceWire | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReferralRedemptionWire[]>([]);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState(true);
  const [mids, setMids] = useState<MerchantMIDWire[]>([]);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const messageOf = useCallback(
    (reason: unknown, fallback: string): string => (reason instanceof ApiError ? reason.message : fallback),
    [],
  );

  const loadBalance = useCallback(async () => {
    setBalanceError(null);
    try {
      setBalance(await getMyReferralBalance());
    } catch (err) {
      setBalance(null);
      setBalanceError(messageOf(err, "Failed to load your credit balance"));
    }
  }, [messageOf]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    setRowsError(null);
    try {
      const data = await listMyRedemptions();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRowsError(messageOf(err, "Failed to load your redemptions"));
    } finally {
      setLoadingRows(false);
    }
  }, [messageOf]);

  const loadMids = useCallback(async () => {
    try {
      const data = await listMyMIDs();
      setMids(Array.isArray(data) ? data : []);
    } catch {
      // The history's Merchant account column degrades to em dashes (the
      // column test pins exactly that); it must never block the table.
      setMids([]);
    }
  }, []);

  useEffect(() => {
    loadBalance();
    loadRows();
    // TD-605: a member without manage-mids never reads the account list -- the
    // history's Merchant account column then shows its em dashes (the same
    // degradation a refused read already produced, minus the 403).
    if (canViewAccounts) loadMids();
  }, [loadBalance, loadRows, loadMids, canViewAccounts]);

  // RedeemDialog success: prepend the created row (§59 — state from the
  // mutation response) and re-read the balance (the spendable figure moved).
  const handleOpened = useCallback(
    (row: ReferralRedemptionWire) => {
      setRows((cur) => [row, ...cur]);
      loadBalance();
    },
    [loadBalance],
  );

  const handleCancel = useCallback(
    async (row: ReferralRedemptionWire) => {
      setCancellingId(row.id);
      setCancelError(null);
      try {
        const cancelled = await cancelMyRedemption(row.id, row.version);
        // §59: re-seed from the response — no list re-fetch.
        setRows((cur) => cur.map((r) => (r.id === cancelled.id ? cancelled : r)));
        loadBalance();
      } catch (err) {
        setCancelError(messageOf(err, "Failed to cancel this request"));
      } finally {
        setCancellingId(null);
      }
    },
    [loadBalance, messageOf],
  );

  const midNumbers = new Map(mids.map((m) => [m.id, m.mid_number]));
  const spendable = spendableOf(balance);
  const holding = holdingOf(balance);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      <EmbedCard>
        <div style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.lg, alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs }}>
            <EmbedText size="xs" tone="secondary">Available credit</EmbedText>
            {/* The figure is its own element so tests (and copy) can pin the
                amount without the label prose; display-small (22px) is the
                plan's figure size — the pre-task lines were 14/16px. */}
            <span data-testid="redemptions-available" style={{ fontSize: t.fontSize.xl, fontWeight: 600, lineHeight: 1.2 }}>
              {spendable === null ? "—" : formatCurrency(spendable)}
            </span>
          </div>
          {/* A zero holding renders NO Maturing line — never "+ $0.00". */}
          {holding !== null && holding > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs }}>
              <EmbedText size="xs" tone="secondary">Maturing</EmbedText>
              <span data-testid="redemptions-holding" style={{ fontSize: t.fontSize.xl, fontWeight: 600, lineHeight: 1.2 }}>
                {formatCurrency(holding)}
              </span>
              <EmbedText size="xs" tone="secondary">
                Still maturing — available once the holding period ends.
              </EmbedText>
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <EmbedButton type="button" onClick={() => setRedeemOpen(true)}>
              Redeem credits
            </EmbedButton>
          </div>
        </div>
        {balanceError && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedBanner tone="error">{balanceError}</EmbedBanner>
            <div style={{ marginTop: t.spacing.md }}>
              <EmbedButton type="button" variant="secondary" onClick={loadBalance}>
                Retry
              </EmbedButton>
            </div>
          </div>
        )}
      </EmbedCard>

      <EmbedCard>
        <EmbedHeading level={2}>Redemptions</EmbedHeading>
        <div style={{ marginTop: t.spacing.sm }}>
          <EmbedText tone="secondary">
            Your redemption history. Once Aldelo verifies a request against
            your processing statement, the verified amount is the figure
            applied — when it differs from what you requested, both are
            shown.
          </EmbedText>
        </div>
        {cancelError && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedBanner tone="error">{cancelError}</EmbedBanner>
          </div>
        )}
        {rowsError && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedBanner tone="error">{rowsError}</EmbedBanner>
            <div style={{ marginTop: t.spacing.md }}>
              <EmbedButton type="button" variant="secondary" onClick={loadRows}>
                Retry
              </EmbedButton>
            </div>
          </div>
        )}
        {!loadingRows && !rowsError && rows.length === 0 && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedText tone="secondary">No redemptions yet.</EmbedText>
          </div>
        )}
        {!rowsError && rows.length > 0 && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedTable
              caption="Your redemption requests"
              columns={[
                {
                  id: "statement",
                  header: "Statement",
                  primary: true,
                  cell: (r) => (
                    // RFC3339 → the statement formatter ("Aug 2026
                    // statement"), never the raw instant (the pre-task card's
                    // YYYY-MM-DD prefix retired with it).
                    <span>{formatStatementMonth(r.statement_month)}</span>
                  ),
                },
                {
                  id: "account",
                  header: "Merchant account",
                  cell: (r) => <span>{midNumbers.get(r.mid_id) ?? "—"}</span>,
                },
                {
                  id: "amount",
                  header: "Amount",
                  cell: (r) => (
                    <span style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs, minWidth: 0 }}>
                      <RedemptionAmounts row={r} />
                      {r.rejected_reason && (
                        <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>
                          {r.rejected_reason}
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: "status",
                  header: "Status",
                  cell: (r) => <EmbedStatusBadge domain="redemption" value={r.status} />,
                },
                {
                  id: "actions",
                  header: "",
                  align: "end",
                  stackedLabel: false,
                  // §4.4.1: pending is the only merchant-cancellable status.
                  cell: (r) =>
                    r.status === "pending" ? (
                      <EmbedLink
                        type="button"
                        disabled={cancellingId !== null}
                        onClick={() => handleCancel(r)}
                      >
                        {cancellingId === r.id ? "Cancelling..." : "Cancel request"}
                      </EmbedLink>
                    ) : null,
                },
              ]}
              rows={rows}
              rowKey={(r) => r.id}
              rowTestId={(r) => `redemption-row-${r.id}`}
            />
          </div>
        )}
      </EmbedCard>

      <RedeemDialog
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        balance={balance}
        onOpened={handleOpened}
        onNavigateSection={onNavigateSection}
        canViewAccounts={canViewAccounts}
      />
    </div>
  );
}
